import React, { useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  TextField,
  Dialog,
  Typography,
  FormControl,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  AutocompleteRenderInputParams,
} from '@mui/material'
import {
  AddCircle,
  ReceiptOutlined,
  Work,
  InfoOutlined,
  Group as GroupIcon,
} from '@mui/icons-material'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import FullWidthField from 'src/common/FullWidthField'
import SelectInput from 'src/common/SelectInput/SelectInput'
import { searchCases } from 'src/services/SearchService'
import { fetchCaseStatuses, createCaseWithPatient } from 'src/services/WorkflowService'
import { getReferralSourceOptions } from 'src/services/CaseService'
import { ReferralSourceOption } from 'src/viewModels/workflow/ReferralSourceOption'
import { pagedList } from 'src/viewModels/pagedList'
import { CaseSummaryResult } from 'src/obmCaseSearch/CaseSearchRequest'
import { CaseStatus } from './CaseStatus'
import States from 'src/utils/States'
import PhoneNumberMaskOBM from 'src/common/PhoneNumberMaskOBM'
import moment from 'moment'
import InjectedActionLogModal from 'src/workflow/modals/shared/ActionLogModal'
import { InboxItemType } from 'src/utils/InboxItemType'
import PatientInvoiceDetailsModal from 'src/obmCaseSearch/PatientInvoiceDetailsModal'

const initialModalValues = {
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  cellPhone: '',
  homePhone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  notes: '',
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  dob: Yup.string()
    .nullable()
    .test('valid-year', 'Year must be between 1900 and current year', (value) => {
      if (!value) return true

      const yearMatch = value.match(/^(\d{1,4})-/)
      if (!yearMatch) return false

      const year = parseInt(yearMatch[1], 10)
      const currentYear = new Date().getFullYear()

      return yearMatch[1].length === 4 && year >= 1900 && year <= currentYear
    })
    .test('valid-date', 'Date of birth cannot be in the future', (value) => {
      if (!value) return true
      const date = new Date(value)
      return date <= new Date()
    }),
})

const CaseSearch: React.FC = () => {
  // GRID/PAGINATION
  const [gridData, setGridData] = useState<CaseSummaryResult[]>(new pagedList().items)
  const [gridLoading, setGridLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)

  // SEARCH
  const [patientFirstName, setPatientFirstName] = useState('')
  const [patientLastName, setPatientLastName] = useState('')
  const [caseNumber, setCaseNumber] = useState('')
  const [sourceContactName, setSourceContactName] = useState('')
  const [sourceName, setSourceName] = useState('')
  const [referralSourceOptions, setReferralSourceOptions] = useState<
    ReferralSourceOption[]
  >([])
  const [hasBalanceDue, setHasBalanceDue] = useState<boolean | undefined>(undefined)
  const [caseStatus, setCaseStatus] = useState<CaseStatus>()
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>()
  const [hasSearched, setHasSearched] = useState(false)

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false)
  const [invoiceDetailId, setInvoiceDetailId] = useState<any>(null)
  const [openModal, setOpenModal] = useState(false)
  const [openActionLogModal, setOpenActionLogModal] = useState(false)
  const [actionLogAssociatedId, setActionLogAssociatedId] = useState('')
  const [caseStatusOptions, setCaseStatusOptions] = useState<
    { value: string; label: string }[]
  >([])

  const getValue = (item: string) => item

  const setPaginationOffsetData = (total: number) => {
    const first = (page - 1) * pageSize + 1
    const last = page * pageSize < total ? page * pageSize : total
    setFirstRecordIndex(first)
    setLastRecordIndex(last)
  }

  const states: string[] = States.map((state) => state.name)
  const dropdownState: string[] = [...[' '], ...states]

  const isSearchGridDataEmpty = () => !gridData || gridData.length === 0

  const paginationInfo = () => {
    if (!isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const mapSearchGridData = () =>
    isSearchGridDataEmpty() ? (
      <TableRow>
        <TableCell colSpan={10} align="center">
          No records found
        </TableCell>
      </TableRow>
    ) : (
      gridData.map((item, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.open(`/workflow/case/${item.id}/default-inbox`)}
            >
              Open
            </Button>
          </TableCell>
          <TableCell>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {item.caseNumber}
              &nbsp;&nbsp;
              <Tooltip title="Show Workflow Action Log">
                <ReceiptOutlined
                  style={{ cursor: 'pointer' }}
                  fontSize="small"
                  onClick={() => {
                    setActionLogAssociatedId(item.id)
                    setOpenActionLogModal(true)
                  }}
                />
              </Tooltip>
            </span>
          </TableCell>
          <TableCell>{item.currentStatusDescription}</TableCell>
          <TableCell>{item.currentInbox}</TableCell>
          <TableCell>
            {Array.isArray(item.sourceName)
              ? item.sourceName.join(', ')
              : item.sourceName || ''}
          </TableCell>
          <TableCell>{item.patientName}</TableCell>
          <TableCell style={{ textAlign: 'right' }}>
            {item.patientDOB ? moment.utc(item.patientDOB).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell style={{ textAlign: 'right' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              {item.numberOfInvoices}
              &nbsp;&nbsp;
              {item.numberOfInvoices > 0 && (
                <Tooltip title="Show Case Line Items">
                  <InfoOutlined
                    style={{ cursor: 'pointer', marginLeft: 8 }}
                    fontSize="small"
                    onClick={() => {
                      setInvoiceDetailId(item.id)
                      setInvoiceModalOpen(true)
                    }}
                  />
                </Tooltip>
              )}
            </span>
          </TableCell>
          <TableCell style={{ textAlign: 'right' }}>
            {item.totalInvoiced !== undefined
              ? `$${item.totalInvoiced.toLocaleString()}`
              : ''}
          </TableCell>
          <TableCell style={{ textAlign: 'right' }}>
            {item.balanceDue !== undefined ? `$${item.balanceDue.toLocaleString()}` : ''}
          </TableCell>
        </TableRow>
      ))
    )

  const clearSearch = async () => {
    setPatientFirstName('')
    setPatientLastName('')
    setCaseNumber('')
    setCaseStatus(undefined)
    setInvoiceNumber(undefined)
    setSourceName('')
    setSourceContactName('')
    setHasBalanceDue(undefined)
    setPage(1)
    setTotalItems(0)
    setPageCount(0)
    setGridData([])
    setGridLoading(false)
  }

  const search = async (event: React.FormEvent) => {
    event.preventDefault()
    setGridLoading(true)
    setPage(1)
    setHasSearched(true)
    try {
      const response = await searchCases(
        patientFirstName.trim() || null,
        patientLastName.trim() || null,
        caseNumber.trim() || null,
        (caseStatus as unknown as CaseStatus) || undefined,
        invoiceNumber || null,
        sourceName.trim() || null,
        sourceContactName.trim() || null,
        hasBalanceDue,
        1,
        pageSize
      )
      const { items, totalItems, totalPages } = response
      setTotalItems(totalItems)
      setPageCount(totalPages)
      setGridData(items)
      if (totalItems > 0) setPaginationOffsetData(totalItems)
    } finally {
      setGridLoading(false)
    }
  }

  const loadCaseGrid = async () => {
    setGridLoading(true)
    const response = await searchCases(
      patientFirstName.trim() || null,
      patientLastName.trim() || null,
      caseNumber.trim() || null,
      (caseStatus as unknown as CaseStatus) || undefined,
      invoiceNumber || null,
      sourceName.trim() || null,
      sourceContactName.trim() || null,
      hasBalanceDue,
      page,
      pageSize
    )
    const { items, totalItems, totalPages } = response
    setTotalItems(totalItems)
    setPageCount(totalPages)
    setGridData(items)
    if (totalItems > 0) setPaginationOffsetData(totalItems)
    setGridLoading(false)
  }

  const handlePageChange = async (event: any) => {
    const newPage = event.selected + 1
    setPage(newPage)
    searchCases(
      patientFirstName.trim() || null,
      patientLastName.trim() || null,
      caseNumber.trim() || null,
      (caseStatus as unknown as CaseStatus) || undefined,
      invoiceNumber || null,
      sourceName.trim() || null,
      sourceContactName.trim() || null,
      hasBalanceDue,
      newPage,
      pageSize
    )
  }

  const handleAddClick = () => setOpenModal(true)
  const handleCloseModal = () => setOpenModal(false)
  const handleCloseInvoiceModal = () => setInvoiceModalOpen(false)
  const handleCloseHistoryModal = () => {
    setActionLogAssociatedId('')
    setOpenActionLogModal(false)
  }

  const handleSave = async (values: any, { setSubmitting }: any) => {
    setGridLoading(true)
    try {
      const requestBody = {
        patientFirstName: values.firstName,
        patientLastName: values.lastName,
        gender: values.gender === 'Male' ? 'M' : values.gender === 'Female' ? 'F' : '',
        patientPhoneC: values.cellPhone || '',
        patientPhoneH: values.homePhone || '',
        dob: values.dob ? new Date(values.dob).toISOString() : undefined,
        patientAddress: values.address1 || '',
        patientCity: values.city || '',
        patientState: values.state || '',
        patientZip: values.zip || '',
        notes: values.notes || '',
      }

      const inboxItem = await createCaseWithPatient(requestBody)
      const caseId = inboxItem.case?.id

      handleCloseModal()

      window.open(`/workflow/case/${caseId}/inbox/${inboxItem.inboxId}`)
    } finally {
      setGridLoading(false)
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const SourceOptions = await getReferralSourceOptions()
      const enabledSources = (SourceOptions || []).filter((opt) => opt.isEnabled)

      const trimmedSources = enabledSources.map((source) => ({
        ...source,
        description: source.description.trim(),
      }))

      const uniqueSources = trimmedSources.filter(
        (source, index, self) =>
          index === self.findIndex((s) => s.description === source.description)
      )

      uniqueSources.sort((a, b) => a.description.localeCompare(b.description))

      setReferralSourceOptions(uniqueSources)

      const loadCaseStatuses = async () => {
        const statuses = await fetchCaseStatuses()
        setCaseStatusOptions(statuses)
      }
      await loadCaseStatuses()
      if (hasSearched) {
        await loadCaseGrid()
      }
    }
    fetchData()
  }, [page, pageSize, hasSearched])

  return (
    <>
      <Helmet>
        <title>Case</title>
      </Helmet>
      <ResponsiveAppBar title="Cases" pageIcon={<Work />} />
      <Paper style={{ margin: 20, height: '100%', paddingBottom: 20 }}>
        {gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <form onSubmit={search}>
          <Grid container style={{ padding: 10, width: '100%' }}>
            <Typography
              color="primary"
              variant="h6"
              gutterBottom
              style={{ paddingBottom: 10 }}
            >
              SEARCH CRITERIA
            </Typography>
            <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={patientFirstName}
                    size="small"
                    onChange={(e) => setPatientFirstName(e.target.value)}
                    label="First Name"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={patientLastName}
                    size="small"
                    onChange={(e) => setPatientLastName(e.target.value)}
                    label="Last Name"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={caseNumber}
                    size="small"
                    onChange={(e) => setCaseNumber(e.target.value)}
                    label="Case Number"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <TextField
                    value={invoiceNumber === undefined ? '' : invoiceNumber}
                    size="small"
                    onChange={(e) =>
                      setInvoiceNumber(e.target.value === '' ? undefined : e.target.value)
                    }
                    label="Invoice #"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid
                item
                xs
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title="Add Case">
                  <AddCircle
                    color="primary"
                    style={{ fontSize: 30, cursor: 'pointer' }}
                    onClick={handleAddClick}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              style={{ width: '100%', marginTop: 10 }}
            >
              <Grid item xs={2}>
                <Autocomplete
                  options={caseStatusOptions}
                  getOptionLabel={(option: { value: string; label: string }) =>
                    option.label
                  }
                  size="small"
                  value={
                    caseStatusOptions.find((opt) => opt.value === String(caseStatus)) ||
                    null
                  }
                  onChange={(_: any, newValue: { value: string; label: string } | null) =>
                    setCaseStatus(
                      newValue ? (newValue.value as unknown as CaseStatus) : undefined
                    )
                  }
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Case Status"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={referralSourceOptions || []}
                  getOptionLabel={(option: ReferralSourceOption) => option.description}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    referralSourceOptions?.find(
                      (opt) => opt.description === sourceName
                    ) || null
                  }
                  onChange={(_: any, newValue: ReferralSourceOption | null) =>
                    setSourceName(newValue ? newValue.description : '')
                  }
                  size="small"
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Referral Source"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={sourceContactName}
                    onChange={(e) => setSourceContactName(e.target.value)}
                    label="Referral Source Contact"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="hasBalanceDue"
                      title="Has Balance Due"
                      checked={!!hasBalanceDue}
                      onChange={(e) =>
                        setHasBalanceDue(e.target.checked ? true : undefined)
                      }
                    />
                  }
                  label="Has Balance Due"
                />
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              style={{
                marginLeft: 1,
                marginTop: 10,
                paddingTop: 5,
                paddingBottom: 10,
                width: '100%',
              }}
            >
              <Button
                type="submit"
                color="primary"
                size="medium"
                variant="contained"
                style={{ marginRight: 10 }}
              >
                Search
              </Button>
              <Button
                color="secondary"
                onClick={clearSearch}
                size="medium"
                variant="contained"
              >
                Clear Search
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12}>
            <Table aria-label="simple table" style={{ padding: 20 }}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell></TableCell>
                  <TableCell style={{ width: '10%' }}>Case #</TableCell>
                  <TableCell style={{ width: '10%' }}>Status</TableCell>
                  <TableCell style={{ width: '15%' }}>Inbox</TableCell>
                  <TableCell style={{ width: '20%' }}>Referral Source</TableCell>
                  <TableCell style={{ width: '10%' }}>Patient</TableCell>
                  <TableCell style={{ width: '10%', textAlign: 'right' }}>
                    Date of Birth
                  </TableCell>
                  <TableCell style={{ width: '10%', textAlign: 'right' }}>
                    # of Line Items
                  </TableCell>
                  <TableCell style={{ width: '10%', textAlign: 'right' }}>
                    Total Invoiced
                  </TableCell>
                  <TableCell
                    style={{ width: '15%', textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    Balance Due
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{mapSearchGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                forcePage={page - 1}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                renderOnZeroPageCount={() => null}
              />
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            overflowX: 'hidden',
          },
        }}
      >
        <ResponsiveAppBar title="Add Case" pageIcon={<GroupIcon />} isModal={true} />

        <Formik
          initialValues={initialModalValues}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ isSubmitting, isValid, setFieldValue, values }) => (
            <Form>
              <Grid container direction="row" xs={12} spacing={2}>
                {/* Patient Details Column */}
                <Grid item xs={6}>
                  <Typography
                    variant="h6"
                    color="textSecondary"
                    style={{
                      height: 18,
                      paddingLeft: 20,
                      fontSize: 18,
                      paddingBottom: 20,
                    }}
                  >
                    PATIENT DETAILS
                  </Typography>
                  <Grid container direction="row">
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="firstName"
                          size="small"
                          label="Patient First Name"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          required
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="lastName"
                          size="small"
                          label="Patient Last Name"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          required
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                  <Grid container direction="row">
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '18px 16px' }}>
                        <SelectInput
                          inputId="gender"
                          getName={getValue}
                          getValue={getValue}
                          label="Gender"
                          name="gender"
                          shrinkLabel={true}
                          fullWidth
                          outlined={true}
                          multiple={false}
                          native={true}
                          items={['Male', 'Female']}
                          onChange={(event) => {
                            setFieldValue('gender', event.target.value)
                          }}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="dob"
                          size="small"
                          label="Date of Birth"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                  <Grid container direction="row">
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="homePhone"
                          size="small"
                          label="Home Phone Number"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          InputProps={{
                            inputComponent: PhoneNumberMaskOBM,
                            maxLength: 10,
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="cellPhone"
                          size="small"
                          label="Cell Phone Number"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          InputProps={{
                            inputComponent: PhoneNumberMaskOBM,
                            maxLength: 10,
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                </Grid>
                {/* Address Details Column */}
                <Grid item xs={6}>
                  <Typography
                    variant="h6"
                    color="textSecondary"
                    style={{
                      height: 18,
                      paddingLeft: 20,
                      fontSize: 18,
                      paddingBottom: 20,
                    }}
                  >
                    ADDRESS DETAILS
                  </Typography>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="address1"
                      size="small"
                      label="Address"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                  <Grid container direction="row">
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="city"
                          size="small"
                          label="City"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          inputProps={{ maxLength: 30 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item xs={3}>
                      <Autocomplete
                        options={dropdownState}
                        size="small"
                        style={{ paddingTop: 16 }}
                        getOptionLabel={(option: string) => option}
                        value={dropdownState.find((opt) => opt === values.state) || null}
                        onChange={(_: any, newValue: string | null) =>
                          setFieldValue('state', newValue || '')
                        }
                        renderInput={(params: AutocompleteRenderInputParams) => (
                          <TextField
                            {...params}
                            label="State"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="zip"
                          size="small"
                          label="Zip Code"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          inputProps={{ maxLength: 10 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container xs={12}>
                <DialogContent style={{ padding: '8px 16px' }}>
                  <FullWidthField
                    InputLabelProps={{ shrink: true }}
                    name="notes"
                    variant="outlined"
                    label="Case Notes"
                    fullWidth
                    multiline
                    minRows={4}
                  />
                </DialogContent>
              </Grid>
              <DialogActions>
                <Button onClick={handleCloseModal} color="primary">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      {actionLogAssociatedId !== '' && (
        <InjectedActionLogModal
          inboxType={InboxItemType.Case}
          isOpen={openActionLogModal}
          associatedItemId={actionLogAssociatedId}
          closeModal={handleCloseHistoryModal}
        />
      )}
      <PatientInvoiceDetailsModal
        open={invoiceModalOpen}
        detail={invoiceDetailId}
        caseSummary={
          invoiceDetailId != null
            ? gridData.find((item) => Number(item.id) === Number(invoiceDetailId))
            : undefined
        }
        onClose={handleCloseInvoiceModal}
      />
    </>
  )
}

export default CaseSearch
